/**
 * BirthDateStep - Step 2 of wizard
 *
 * Handles birth date verification with YYYY / MM / DD separated inputs
 */

import { useState, useRef, useCallback } from 'react';
import { getEnv } from '@/lib/env';
import { useFormContext } from 'react-hook-form';
import type { StepProps, Step2Response } from '../types';
import type { BirthDateFormData } from '../validation/schemas';
import { useSettings } from '@/contexts/SettingsContext';
import { BIRTH_DATE_STEP, BIRTH_DATE_VALIDATION } from '@/constants/translations';
import './BirthDateStep.css';

interface BirthDateStepProps extends StepProps {
  sessionId: string;
  step1Token: string;
  participantMaskedName: string;
}

export function BirthDateStep({
  sessionId,
  step1Token,
  participantMaskedName,
  onNext,
}: BirthDateStepProps) {
  const { settings } = useSettings();
  const t = BIRTH_DATE_STEP[settings.language];
  const v = BIRTH_DATE_VALIDATION[settings.language];

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
    setError,
    clearErrors,
  } = useFormContext<BirthDateFormData>();

  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [apiError, setApiError] = useState<string>('');
  const [fieldError, setFieldError] = useState<string>('');

  const yearRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);

  /**
   * Sync combined value into react-hook-form
   */
  const syncValue = useCallback((y: string, m: string, d: string) => {
    const combined = `${y}${m}${d}`;
    setValue('birthDate', combined, { shouldValidate: false });
    clearErrors('birthDate');
    setFieldError('');
    setApiError('');
  }, [setValue, clearErrors]);

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(val);
    syncValue(val, month, day);
    if (val.length === 4) {
      monthRef.current?.focus();
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMonth(val);
    syncValue(year, val, day);
    if (val.length === 2) {
      dayRef.current?.focus();
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDay(val);
    syncValue(year, month, val);
  };

  /**
   * Backspace on empty month → focus year
   */
  const handleMonthKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && month === '') {
      yearRef.current?.focus();
    }
  };

  /**
   * Backspace on empty day → focus month
   */
  const handleDayKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && day === '') {
      monthRef.current?.focus();
    }
  };

  /**
   * Validate fields before submission
   */
  const validate = (): string | null => {
    if (year.length !== 4) return v.yearRequired;
    if (month.length !== 2) return v.monthRequired;
    if (day.length !== 2) return v.dayRequired;

    const mm = parseInt(month, 10);
    const dd = parseInt(day, 10);
    if (mm < 1 || mm > 12) return v.invalidMonth;
    if (dd < 1 || dd > 31) return v.invalidDay;

    return null;
  };

  /**
   * Handle form submission
   */
  const onSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setFieldError(validationError);
      return;
    }

    const birthDateValue = `${year}${month}${day}`;

    try {
      setApiError('');
      setFieldError('');

      const apiUrl = getEnv('API_URL') || 'http://localhost:43011';
      const response = await fetch(
        `${apiUrl}/patient/auth/session/${sessionId}/verify-activation-step2`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            step1_token: step1Token,
            birth_date: birthDateValue,
          }),
        }
      );

      const result: Step2Response = await response.json();

      if (!response.ok || !result.success) {
        const errorMessage = getErrorMessage(result.error);
        setApiError(errorMessage);
        setError('birthDate', { message: errorMessage });
        return;
      }

      await onNext(result);
    } catch {
      setApiError(t.errors.network);
    }
  };

  /**
   * Convert API error codes to user-friendly messages
   */
  const getErrorMessage = (error?: string): string => {
    switch (error) {
      case 'STEP1_TOKEN_INVALID':
        return t.errors.sessionExpired;
      case 'STEP1_TOKEN_EXPIRED':
        return t.errors.authTimeout;
      case 'BIRTHDATE_MISMATCH':
        return t.errors.mismatch;
      case 'SESSION_NOT_FOUND':
        return t.errors.surveyNotFound;
      default:
        return t.errors.authFailed;
    }
  };

  const errorMessage = fieldError || apiError;
  const isComplete = year.length === 4 && month.length === 2 && day.length === 2;

  return (
    <div className="birth-date-step">
      <div className="step-card">
        <h2 className="step-title">{t.title}</h2>
        <p className="step-description">
          {t.description(participantMaskedName)}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="step-form">
          <div className="form-group">
            <div className="date-input-group">
              {/* 년 */}
              <div className="date-field">
                <input
                  ref={yearRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="YYYY"
                  value={year}
                  onChange={handleYearChange}
                  disabled={isSubmitting}
                  className="date-input year-input"
                  autoFocus
                  autoComplete="off"
                />
                <span className="date-label">{v.yearLabel}</span>
              </div>

              <span className="date-separator">/</span>

              {/* 월 */}
              <div className="date-field">
                <input
                  ref={monthRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder="MM"
                  value={month}
                  onChange={handleMonthChange}
                  onKeyDown={handleMonthKeyDown}
                  disabled={isSubmitting}
                  className="date-input month-input"
                  autoComplete="off"
                />
                <span className="date-label">{v.monthLabel}</span>
              </div>

              <span className="date-separator">/</span>

              {/* 일 */}
              <div className="date-field">
                <input
                  ref={dayRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder="DD"
                  value={day}
                  onChange={handleDayChange}
                  onKeyDown={handleDayKeyDown}
                  disabled={isSubmitting}
                  className="date-input day-input"
                  autoComplete="off"
                />
                <span className="date-label">{v.dayLabel}</span>
              </div>
            </div>

            {errorMessage && (
              <p className="form-error">{errorMessage}</p>
            )}

            <p className="form-hint">{t.hint}</p>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !isComplete}
            >
              {isSubmitting ? t.submitting : t.next}
            </button>
          </div>
        </form>

        <div className="step-help">
          <p>{t.helpText}</p>
        </div>
      </div>
    </div>
  );
}
